<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Employee Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.employeeName')"
                v-model.trim="filterOptions.name"
              />
              <!-- End:: Employee Name Input -->

              <!-- Start:: Phone Input -->
              <base-input
                col="4"
                type="tel"
                :placeholder="$t('PLACEHOLDERS.phone')"
                v-model.trim="filterOptions.phone"
              />
              <!-- End:: Phone Input -->

              <!-- Start:: Status Input -->
              <base-select-input
                col="4"
                :optionsList="messageStatuses"
                :placeholder="$t('PLACEHOLDERS.status')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Input -->
            </div>

            <div class="btns_wrapper">
              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.search") }}</span>
                </template>
                <span
                  class="submit_btn"
                  @click="submitFilterForm"
                  :disabled="isWaitingRequest"
                >
                  <i class="fal fa-search"></i>
                </span>
              </a-tooltip>

              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.rseet_search") }}</span>
                </template>
                <span
                  class="reset_btn"
                  :disabled="isWaitingRequest"
                  @click="resetFilter"
                >
                  <i class="fal fa-redo"></i>
                </span>
              </a-tooltip>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.support_management") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ noDataMessage }}
        </template>
        <!-- End:: No Data State -->

        <template v-slot:[`item.id`]="{ item, index }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.id">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>

        <!-- Start:: Employee Name -->
        <template v-slot:[`item.employee_name`]="{ item }">
          <h6 class="text-danger" v-if="!item.user || !item.user.name">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.user.name }}</h6>
        </template>
        <!-- End:: Employee Name -->

        <!-- Start:: Job Name -->
        <template v-slot:[`item.job_name`]="{ item }">
          <h6
            class="text-danger"
            v-if="!item.user || !item.user.job || !item.user.job.name"
          >
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.user.job.name }}</h6>
        </template>
        <!-- End:: Job Name -->

        <!-- Start:: Phone -->
        <template v-slot:[`item.mobile`]="{ item }">
          <h6 class="text-danger" v-if="!item.user || !item.user.mobile">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else dir="ltr">{{ item.user.mobile }}</h6>
        </template>
        <!-- End:: Phone -->

        <!-- Start:: Last Message -->
        <template v-slot:[`item.last_message`]="{ item }">
          <h6
            class="text-danger"
            v-if="!item.last_message || !item.last_message.message_text"
          >
            {{ $t("TABLES.noData") }}
          </h6>
          <div v-else class="message_preview">
            {{ truncateText(item.last_message.message_text, 50) }}
          </div>
        </template>
        <!-- End:: Last Message -->

        <!-- Start:: Message Date -->
        <template v-slot:[`item.message_date`]="{ item }">
          <h6
            class="text-danger"
            v-if="!item.last_message || !item.last_message.created_at"
          >
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.last_message.created_at }}</h6>
        </template>
        <!-- End:: Message Date -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            :color="
              item.last_message && item.last_message.read_at
                ? 'green'
                : 'orange'
            "
            text-color="white"
            small
          >
            {{
              item.last_message && item.last_message.read_at
                ? $t("STATUS.replied")
                : $t("STATUS.notReplied")
            }}
          </v-chip>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip placement="bottom" v-if="$can('chats index', 'chats')">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="bottom"
              v-if="
                $can('clients reply', 'clients') &&
                (!item.last_message || !item.last_message.read_at)
              "
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.replay") }}</span>
              </template>
              <button class="btn_primary" @click="openReplyModal(item)">
                <i class="fal fa-reply"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Reply Modal -->
    <v-dialog v-model="replyModalIsOpen" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 justify-center">
          {{ $t("TITLES.sendReplay") }}
        </v-card-title>

        <v-card-text>
          <form @submit.prevent="handleReply">
            <div class="row">
              <!-- Start:: Reply Message Input -->
              <base-input
                col="12"
                type="textarea"
                rows="4"
                :placeholder="$t('PLACEHOLDERS.messageReplay')"
                v-model.trim="replyFormData.message"
                required
              />
              <!-- End:: Reply Message Input -->
            </div>
          </form>
        </v-card-text>

        <v-card-actions>
          <v-btn
            class="modal_confirm_btn"
            @click="handleReply"
            :disabled="
              !replyFormData.message ||
              replyFormData.message.length < 3 ||
              isWaitingRequest
            "
          >
            {{ $t("BUTTONS.send") }}
          </v-btn>

          <v-btn
            class="modal_cancel_btn"
            @click="replyModalIsOpen = false"
            :disabled="isWaitingRequest"
          >
            {{ $t("BUTTONS.cancel") }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End:: Reply Modal -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllSupportChat",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    messageStatuses() {
      return [
        {
          id: 1,
          name: this.$t("STATUS.replied"),
          value: "1",
        },
        {
          id: 2,
          name: this.$t("STATUS.notReplied"),
          value: "0",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        name: null,
        phone: null,
        status: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      noDataMessage: this.$t("TABLES.noData"),
      tableHeaders: [
        {
          text: this.$t("TABLES.Clients.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.employeeName"),
          value: "employee_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.jobName"),
          value: "job_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Clients.phone"),
          value: "mobile",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.lastMessage"),
          value: "last_message",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.messageDate"),
          value: "message_date",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Clients.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data

      // Start:: Reply Modal Data
      replyModalIsOpen: false,
      selectedChat: null,
      replyFormData: {
        message: null,
      },
      // End:: Reply Modal Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Truncate Text
    truncateText(text, length) {
      if (!text) return "-";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
    // End:: Truncate Text

    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/support-chat/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.name = null;
      this.filterOptions.phone = null;
      this.filterOptions.status = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/support-chat/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "chats",
          params: {
            // page: this.paginations.current_page,
            name: this.filterOptions.name,
            mobile: this.filterOptions.phone,
            is_replied: this.filterOptions.status?.value,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data;

        // Calculate pagination if meta is available
        if (res.data.meta) {
          this.paginations.last_page = res.data.meta.last_page || 1;
          this.paginations.items_per_page = res.data.meta.per_page || 10;
        }

        if (
          this.tableRows.length === 0 &&
          (this.filterOptions.name ||
            this.filterOptions.phone ||
            this.filterOptions.status)
        ) {
          this.noDataMessage = this.$t("MESSAGES.noSearchResults");
        } else {
          this.noDataMessage = this.$t("TABLES.noData");
        }
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
        this.$message.error(this.$t("MESSAGES.errorLoadingChats"));
      }
    },
    // End:: Set Table Rows

    // Start:: Open Reply Modal
    openReplyModal(chat) {
      this.selectedChat = chat;
      this.replyModalIsOpen = true;
    },
    // End:: Open Reply Modal

    // Start:: Handle Reply
    async handleReply() {
      if (
        !this.replyFormData.message ||
        this.replyFormData.message.length < 3
      ) {
        this.$message.error(this.$t("MESSAGES.replyMessageRequired"));
        return;
      }

      this.isWaitingRequest = true;
      const REQUEST_DATA = new FormData();

      REQUEST_DATA.append("message", this.replyFormData.message);
      REQUEST_DATA.append("to_id", this.selectedChat.user.id);
      REQUEST_DATA.append("to_type", "user");

      try {
        await this.$axios({
          method: "POST",
          url: `chats/${this.selectedChat.id}/send-message`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.replyModalIsOpen = false;
        this.replyFormData.message = null;
        this.$message.success(this.$t("MESSAGES.replySentSuccessfully"));
        this.setTableRows();
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Handle Reply

    // ==================== Start:: Crud ====================
    // ===== Start:: Show
    showItem(chat) {
      this.$router.push({ path: `/support-chat/show/${chat.id}` });
    },
    // ===== End:: Show
    // ==================== End:: Crud ====================
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.message_preview {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

span.submit_btn {
  width: 45px;
  height: 45px;
  font-size: 16px;
  border-radius: 10px;
  color: var(--white_clr);
  transition: all 0.3s linear;
  background-color: #f6a513;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.btn_primary {
  background-color: #26a1d6 !important;
  border-color: #26a1d6 !important;
}

.btn_primary:hover {
  background-color: #1e8ab8 !important;
  border-color: #1e8ab8 !important;
}
</style>
